import type { FeatureSection } from './types'

export const tablesSection: FeatureSection = {
  key: 'tables',
  title: 'Tables',
  navPage: 'tables',
  routes: ['tables', 'multitables', 'editspec', 'batch-generate', 'batch-regenerate'],
  audience: 'everyone',
  image: '/help/screenshots/tables/_section.png',
  imageAlt: 'The Tables page with the table tree on the left and the Wave Q1 table open: counts and column percentages by wave.',
  about:
    'Tables are the cross-tabs of a job: the rows (side) against the columns (top), with counts, percentages and significance. The Tables page is where people open a saved table, read it as a table or a chart, filter it, change how it shows, and export it to Excel or PowerPoint. From here they also build or change a table in Edit Spec, group tables into a multi-table list, generate many tables at once, and keep their own tables in My Tables. Everyone signed in to the customer uses it; a few actions are for Managers.',
  features: [
    {
      key: 'table-of-contents',
      name: 'Tables tree',
      what: 'The tree on the left lists every saved table of the job, in folders. Shared tables sit at the top level, your own tables sit in My Tables, colleagues’ My Tables can be read but not changed, and saved multi-table lists sit in My Table Lists.',
      how: 'Click a folder to open it and a table to load it. Type in the search box at the top of the tree (Search tables and folders...) to narrow it by name or by a table’s subtitle (the side title shown under its name); tables whose name matches list first. Each table and folder has a ⋮ menu (More actions) with what you can do to it.',
      tools: ['find_toc_item', 'open_toc_table', 'expand_toc_folder', 'close_table'],
      agentNotes:
        'find_toc_item returns each item’s path and the changes its menu allows this user. Open a table with open_toc_table; close_table returns to the empty page with the recent tables. A colleague’s My Tables is read-only to everyone but its owner.',
      image: '/help/screenshots/tables/table-of-contents.png',
      imageAlt: 'The table tree: Select, the search box, and the Exec, Standard Tables and Waves folders open on their tables.',
    },
    {
      key: 'star-and-recent',
      name: 'Star and Recent Tables',
      what: 'A star marks a table as a favourite. Your ten most recent tables, starred first, show when you hover Tables in the top bar, and under Recent Tables on the Tables page when no table is open.',
      how: 'Click the star beside a table in the Recent Tables list or in the Tables menu of the top bar. Click it again to unstar. The Tables menu also has View all tables and Batch Generate.',
      tools: ['star_table'],
      agentNotes:
        'star_table toggles the star on the open table: on a starred table it removes the star. Stars and the recent list are kept in this browser, not on the account, so they do not follow the person to another browser. Only a recently opened table can carry a star. A deleted table leaves the list; a renamed or moved one follows to its new place.',
      image: '/help/screenshots/tables/star-and-recent.png',
      imageAlt: 'The Tables menu in the top bar: Recent Tables with Wave Q1 and its star, then View all tables and Batch Generate.',
    },
    {
      key: 'rename-move-folders',
      name: 'Rename, Move and folders',
      what: 'Organises tables and folders. In My Tables you can rename and reorder your own tables and folders, move a table into another folder, and make folders and subfolders. A Manager can also rename and move shared tables, rename shared folders and make folders in the shared tables.',
      how: 'Open the ⋮ menu on a table or folder and choose Rename, Move, Move up or Move down. Move is for tables: it opens Move to Folder, where you pick the destination or make a New Subfolder, then click Select. Use New Folder on My Tables (or, for a Manager, on the shared tables) and New Subfolder on a folder, then type the name and click Create.',
      controls: ['Rename', 'Move', 'Move up', 'Move down', 'New Folder', 'New Subfolder'],
      tools: ['rename_toc_item', 'create_folder'],
      agentNotes:
        'rename_toc_item needs the user to click Confirm in the pane; it cannot confirm itself. On a table the user cannot rename, it offers a copy in My Tables under the new name instead. Renaming or moving a My Tables table moves the table itself, and renaming a My Tables folder moves every table in it; when a table is in use by a dashboard or another output, the rename or move is refused and names what uses it. Move on one of your own tables is disabled until My Tables has a folder. Folders cannot be moved with Move, only reordered, and shared folders cannot be reordered. Moving and reordering have no page tool: the person does them.',
      image: '/help/screenshots/tables/rename-move-folders.png',
      imageAlt: 'The ⋮ menu of the Waves folder with Rename highlighted, below New Subfolder.',
    },
    {
      key: 'copy-tables',
      name: 'Duplicate and Make your own copy',
      what: 'Copies a table. Duplicate puts the copy beside the original, so it is offered on your own tables and, for a Manager, on the shared tables. Make your own copy is offered on shared tables and colleagues’ tables: it puts a copy into your My Tables and opens it.',
      how: 'Open the ⋮ menu on a table and choose Duplicate or Make your own copy. Type a name for the copy and click Duplicate or Copy. In the Insights pane, a change you cannot make on a table comes with one-click Duplicate to My Tables.',
      controls: ['Duplicate', 'Make your own copy'],
      tools: ['duplicate_table'],
      agentNotes:
        'duplicate_table with destination my_tables is Make your own copy; without it, Duplicate in the same place. The user must click Confirm in the pane. The copy in My Tables is the user’s to change.',
      image: '/help/screenshots/tables/copy-tables.png',
      imageAlt: 'The ⋮ menu of the Wave Q1 table with Make your own copy highlighted, below Duplicate.',
    },
    {
      key: 'delete',
      name: 'Delete',
      what: 'Deletes a table or a folder. A folder’s Delete removes every table under it, at any depth. You can delete your own tables and folders; a Manager can also delete shared ones and a colleague’s table.',
      how: 'Open the ⋮ menu and choose Delete, then type delete to confirm. For a folder or a shared table, Platinum first counts the tables that would go and says if dashboards or other outputs use any of them. While it deletes, a window counts the tables ("Deleting 40 of 171 tables"). A delete cannot be undone.',
      controls: ['Delete'],
      agentNotes:
        'There is no page tool that deletes anything: deleting stays with the person. Do not claim to have deleted a table. The count in the confirm is Platinum’s own count of every table inside, not what the tree shows open. Deleting a single table in My Tables asks for the word delete but does not check whether a dashboard uses it. A colleague’s folder cannot be deleted by anyone but its owner.',
      image: '/help/screenshots/tables/delete.png',
      imageAlt: 'The ⋮ menu of the Wave Q1 table with Delete highlighted.',
    },
    {
      key: 'select-mode',
      name: 'Select',
      what: 'Deletes several tables and folders at once.',
      how: 'Click Select above the tree. Tick tables and folders; a ticked folder covers everything inside it. Click Delete in the bar above the tree, check the count, type delete and confirm. Click Done selecting to leave Select mode.',
      controls: ['Select', 'Done selecting', 'Delete selected'],
      agentNotes:
        'Ticking and the bulk delete are left to the person; no page tool does them. A row the user may not delete cannot be ticked and shows the reason.',
      image: '/help/screenshots/tables/select-mode.png',
      imageAlt: 'The tree in Select mode: three tables ticked, 3 selected with Delete, and Done selecting.',
    },
    {
      key: 'download-folder',
      name: 'Download all to Excel',
      what: 'Downloads every table in a folder, subfolders included, as one Excel workbook with one sheet per table, in the order of the tree.',
      how: 'Open the ⋮ menu on a folder and choose Download all to Excel. A message shows while the workbook is prepared.',
      controls: ['Download all to Excel'],
      tools: ['download_folder_excel'],
      agentNotes: 'The workbook carries the Platinum output stamp: who made it, when, and which job and folder.',
      image: '/help/screenshots/tables/download-folder.png',
      imageAlt: 'The ⋮ menu of the Waves folder with Download all to Excel highlighted.',
    },
    {
      key: 'tree-context',
      name: 'Context',
      what: 'Opens Configure for a table or folder: the settings and AI context that apply to it, such as the Table Analysis Prompt that Insights reads. A folder’s settings apply to the tables in it unless a table sets its own.',
      how: 'Open the ⋮ menu on a table or folder and choose Context. Change the fields and save.',
      controls: ['Context'],
      agentNotes: 'There is no page tool for this editor; the person edits it.',
      image: '/help/screenshots/tables/tree-context.png',
      imageAlt: 'The ⋮ menu of the Wave Q1 table with Context highlighted.',
    },
    {
      key: 'add-to-multi-table',
      name: 'Add to multi-table',
      what: 'Puts a table into a multi-table list, a named set of tables you read together with one filter.',
      how: 'Open the ⋮ menu on a table and choose Add to multi-table. The Multi-Tables page opens with the table, and the open table too if it is a different one. While a list is open, Remove from multi-table takes a table out of it.',
      controls: ['Add to multi-table', 'Remove from multi-table'],
      image: '/help/screenshots/tables/add-to-multi-table.png',
      imageAlt: 'The ⋮ menu of the Wave Q1 table with Add to multi-table highlighted.',
    },
    {
      key: 'cache-maintenance',
      name: 'Clear and rebuild cache',
      audience: 'internal',
      what: 'Clears or rebuilds the stored results of a folder’s tables, or of all of your My Tables, so they are worked out again from the data. These are for Managers and Realm admins; other people see them disabled with the reason.',
      how: 'Open the ⋮ menu on a folder and choose Clear Folder Cache or Rebuild Folder Cache, or on My Tables choose Rebuild All Cache. A rebuild queues the tables and says how many.',
      controls: ['Clear Folder Cache', 'Rebuild Folder Cache', 'Rebuild All Cache'],
    },
    {
      key: 'cache-switch',
      name: 'Cache',
      audience: 'internal',
      what: 'A switch on the table toolbar that loads the open table without its stored results, to check them.',
      how: 'Turn the Cache switch off to load the table fresh, and on again to use stored results.',
      controls: ['Cache …'],
    },
    {
      key: 'reading-a-table',
      name: 'The table',
      what: 'The open cross-tab: row labels down the side, column labels across the top, and each cell’s figures. The page title is the table’s name and the subtitle its side question.',
      how: 'Open a table from the tree. Click a cell to see where it sits (its column and row labels) and its frequency, column %, row % and significance letters.',
      tools: ['get_page_context', 'read_table_summary', 'read_open_table'],
      agentNotes:
        'read_open_table gives the numbers as markdown, up to 200 rows, for a saved table, a preview or an AutoCoder table. read_table_summary gives the row and column labels exactly as shown; use them word for word when charting or filtering. get_page_context re-reads the customer, job, table and filter.',
      image: '/help/screenshots/tables/reading-a-table.png',
      imageAlt: 'The open Wave Q1 table: the age bands down the side, Count and the three waves across the top, with counts and column percentages.',
    },
    {
      key: 'table-layout',
      name: 'Fullscreen and header size',
      what: 'Changes how much room the table gets: full screen, the height and width of the column and row headers, and how many row labels show.',
      how: 'Use the buttons on the table toolbar. The side labels button cycles from all labels, to hiding group labels, to hiding all side labels, and back.',
      controls: [
        'Enter Fullscreen',
        'Close Fullscreen',
        'Contract Header Height',
        'Expand Header Height',
        'Contract Header Width',
        'Expand Header Width',
        'Hide Group Labels',
        'Hide All Side Labels',
        'Show All Side Labels',
      ],
      tools: ['set_fullscreen', 'set_side_labels_mode'],
      agentNotes: 'Header height and width have no page tool: the person sizes them by eye.',
      image: '/help/screenshots/tables/table-layout.png',
      imageAlt: 'The table toolbar with the pointer on Expand Header Height, beside the other header size buttons.',
    },
    {
      key: 'metadata',
      name: 'Metadata',
      audience: 'internal',
      what: 'Describes the open table: its side and top axes, filter, weighting variable, sample sizes (unweighted and filtered base), significance settings, and when its data was loaded.',
      how: 'Click Metadata on the table or chart toolbar. Close it with the close button.',
      controls: ['Metadata'],
      tools: ['open_metadata'],
    },
    {
      key: 'download-excel',
      name: 'Download to Excel',
      what: 'Downloads the open table as an Excel workbook. Each workbook carries the output stamp: “Generated by Platinum”, your email, the date and time in UTC, and the job and table it came from.',
      how: 'Click Download to Excel on the table toolbar. A message shows while the workbook is prepared.',
      controls: ['Download to Excel'],
      tools: ['export_table_excel'],
      image: '/help/screenshots/tables/download-excel.png',
      imageAlt: 'The table toolbar with the pointer on the Download to Excel button.',
    },
    {
      key: 'chart-view',
      name: 'Chart View',
      what: 'Shows the open table as a chart. You choose the chart type, whether it plots frequencies, column % or row %, which rows and columns it plots, and you can swap rows and columns.',
      how: 'Click Chart View on the table toolbar. Use Chart Type, Data Type, Rows and Columns, and Column for a chart that shows one column. Turn on Switch Axis to swap rows and columns. Click Table View to go back.',
      controls: ['Chart View', 'Table View', 'Chart Type', 'Data Type', 'Column', 'Rows', 'Columns', 'Switch Axis'],
      tools: ['show_chart_view', 'show_table_view', 'set_chart_options', 'render_chart'],
      settings: [
        'Chart Type: Pie, Bar, Column, Line, Stacked Bar, Clustered Bar, Stacked Column or Clustered Column, as the table allows.',
        'Data Type: Frequencies, Column % or Row %.',
      ],
      agentNotes:
        'set_chart_options takes rows and columns by their labels as the table shows them, and refuses a chart type the table cannot be drawn as. Chart choices count as view settings: Save Settings keeps them.',
      image: '/help/screenshots/tables/chart-view.png',
      imageAlt: 'Chart View of the table: a line chart of the age bands by wave, with Chart Type, Data Type, Rows, Columns and Switch Axis.',
    },
    {
      key: 'export-pptx',
      name: 'Export to PowerPoint',
      what: 'Downloads the chart as a PowerPoint slide with a native, editable chart. The slide carries the output stamp: your email, the date and time in UTC, and the job and table.',
      how: 'In Chart View, click Export to PowerPoint.',
      controls: ['Export to PowerPoint'],
      tools: ['export_chart_pptx'],
      image: '/help/screenshots/tables/export-pptx.png',
      imageAlt: 'The chart toolbar with the pointer on Export to PowerPoint.',
    },
    {
      key: 'quick-filter',
      name: 'Quick Filter',
      what: 'Restricts the open table to the respondents a filter selects, without changing the saved table. The filter shows as a chip on the toolbar.',
      how: 'Click Quick Filter. In the Filter Editor, tick variables and codes in the variable tree (Search variables... narrows it), click the green + to add them to the filter expression, or type the expression yourself. Click Save to apply it, or Cancel. Click the x on the filter chip to remove it.',
      controls: [
        'Quick Filter',
        'Quick filter: …',
        'Close filter editor',
        'Apply selected variables to filter',
        'Filter expression...',
        'Cancel',
        'Save',
        'Data source',
        'Search variables...',
      ],
      tools: ['open_quick_filter', 'set_quick_filter', 'clear_quick_filter', 'find_variable_codes'],
      settings: ['Data source: Managers can switch the variable tree between Survey Data and AutoCoder Data.'],
      agentNotes:
        'Look up variables and codes with find_variable_codes; never guess one. set_quick_filter checks the expression and returns its meaning in the survey’s words; put that to the user as a question and apply it only after they say yes (confirmed: true). Syntax: VARIABLE(codes), codes separated by ; (age(1;2)), a/b is a range, * is all codes, & is AND, | is OR. A quick filter saved with Save Table is combined with the table’s own filter.',
      image: '/help/screenshots/tables/quick-filter.png',
      imageAlt: 'The filter editor that Quick Filter opens: the filter expression box and the job’s variable tree.',
    },
    {
      key: 'quick-settings',
      name: 'Quick Settings',
      what: 'A drawer with every setting for how the open table is worked out and shown: cell display, decimals, significance, row and column settings, titles, advanced settings and context.',
      how: 'Click Quick Settings on the table toolbar. Change what you need in its sections. Click Apply to use the changes, or Cancel to close the drawer and drop them. On an unsaved table from Edit Spec, Apply works the table out again with the new settings, keeping its rows, columns, filter and weight. Each Apply builds on the changes before it, until you click Discard.',
      controls: ['Quick Settings', 'Cancel', 'Apply'],
      tools: ['open_quick_settings', 'apply_quick_settings', 'cancel_quick_settings', 'read_quick_settings'],
      agentNotes:
        'The set_* Quick Settings tools open the drawer, make the change and apply it, as Apply does. If the user already has the drawer open, the change waits there for their Apply. Applied settings are not saved to the table until Save Settings or Save Table.',
      image: '/help/screenshots/tables/quick-settings.png',
      imageAlt: 'The Quick Settings drawer with Cancel and Apply, Cell Display at the top and the other sections closed.',
    },
    {
      key: 'cell-display',
      name: 'Cell Display',
      what: 'Chooses which figures each cell shows: frequencies, column percentages and row percentages.',
      how: 'In Quick Settings, switch Frequencies, Column % and Row % on or off, then click Apply.',
      controls: ['Frequencies', 'Column %', 'Row %'],
      tools: ['set_cell_display', 'update_table_settings'],
      image: '/help/screenshots/tables/cell-display.png',
      imageAlt: 'Cell Display in Quick Settings: Frequencies and Column % on, Row % off.',
    },
    {
      key: 'advanced-cell-display',
      name: 'Advanced Cell Display',
      what: 'Fine control of how cells look: the % sign, percentages as proportions, a thousands separator, and how missing, zero and blank cells show.',
      how: 'In Quick Settings, open Advanced Cell Display, change the switches, then click Apply.',
      controls: [
        'Advanced Cell Display',
        '% Sign',
        '% as Proportions',
        '1000s Separator',
        'Missing as Zero',
        'Missing as Blank',
        'Zero as Blank',
        'Blank as Char',
        'Blank Char',
        'Show Redundant 100%',
      ],
      tools: ['set_cell_display'],
      settings: [
        '% as Proportions: shows 0.45 in place of 45%.',
        'Blank as Char and Blank Char: show blank cells as the character you type.',
        'Show Redundant 100%: shows 100% cells that carry no information.',
      ],
      image: '/help/screenshots/tables/advanced-cell-display.png',
      imageAlt: 'Advanced Cell Display in Quick Settings: switches for % Sign, % as Proportions, 1000s Separator, missing and zero display, and Show Redundant 100%.',
    },
    {
      key: 'decimals',
      name: 'Decimals',
      what: 'Sets the number of decimal places, from 0 to 9, for frequencies, percentages, statistics and expressions.',
      how: 'In Quick Settings, open Decimals, set each number, then click Apply.',
      controls: ['Decimals', 'Frequencies', 'Percents', 'Statistics', 'Expressions'],
      tools: ['set_decimals'],
      image: '/help/screenshots/tables/decimals.png',
      imageAlt: 'Decimals in Quick Settings: places for Frequencies, Percents, Statistics and Expressions.',
    },
    {
      key: 'significance',
      name: 'Significance',
      what: 'Turns significance testing on or off and sets how it runs: across column groups or row groups, the test for proportions and for means, the base, the confidence levels and the letters.',
      how: 'In Quick Settings, open Significance, change the settings, then click Apply. Changing any setting turns testing on.',
      controls: [
        'Significance',
        'Visible',
        'Type',
        'Prop Stat',
        'Mean Stat',
        'Auto Param',
        'Stats Param',
        'Append Letters',
        'Headcount',
        'Level 1',
        'Level 2',
        'One-Tailed',
        'Use level 1 only (52 letter tests)',
        'Skip Base < 30',
        'Skip Cell < 5',
      ],
      tools: ['set_significance'],
      settings: [
        'Type: test across column groups or across row groups.',
        'Prop Stat and Mean Stat: Z-test or T-test, for proportions and for means.',
        'Headcount: the base the test uses: weighted, unweighted or effective base.',
        'Level 1 and Level 2: the confidence levels, in percent.',
        'Skip Base < 30 and Skip Cell < 5: leave out small bases and small cells.',
      ],
      image: '/help/screenshots/tables/significance.png',
      imageAlt: 'Significance in Quick Settings: Visible, Type, Prop Stat, Mean Stat, the two levels and the test options.',
    },
    {
      key: 'row-settings',
      name: 'Row Settings (Sort / Hide / Labels)',
      what: 'Sorts and hides the table’s rows, and shows or hides the base count and the row labels.',
      how: 'In Quick Settings, open Row Settings. Turn on Sort Active and choose how to sort, or turn on Hide Active and choose what to hide, then click Apply.',
      controls: [
        'Row Settings (Sort / Hide / Labels)',
        'Sort Active',
        'Increasing',
        'Ungrouped',
        'Type',
        'Band',
        'Key',
        'Hide Active',
        'If data missing',
        'If data equal to zero',
        'Vectors',
        'Base Count',
        'Labels Visible',
        'Groups Visible',
      ],
      tools: ['set_row_settings'],
      settings: [
        'Type: sort by value, code or label.',
        'Band: the figure to sort on: the first shown, frequency, column % or row %.',
        'Key: the column number to sort on.',
        'Vectors: the row numbers to hide, separated by ;, for example 1;3;4.',
        'Labels Visible and Groups Visible: only on tables that have these settings.',
      ],
      image: '/help/screenshots/tables/row-settings.png',
      imageAlt: 'Row Settings in Quick Settings: Sort Active, Hide Active, Base Count, Labels Visible and Groups Visible.',
    },
    {
      key: 'column-settings',
      name: 'Column Settings (Sort / Hide / Labels)',
      what: 'Sorts and hides the table’s columns, and shows or hides the base count and the column labels.',
      how: 'In Quick Settings, open Column Settings. Turn on Sort Active and choose how to sort, or turn on Hide Active and choose what to hide, then click Apply.',
      controls: [
        'Column Settings (Sort / Hide / Labels)',
        'Sort Active',
        'Increasing',
        'Ungrouped',
        'Type',
        'Band',
        'Key',
        'Hide Active',
        'If data missing',
        'If data equal to zero',
        'Vectors',
        'Base Count',
        'Labels Visible',
        'Groups Visible',
      ],
      tools: ['set_column_settings'],
      settings: [
        'Key: the row number to sort on.',
        'Vectors: the column numbers to hide, separated by ;.',
      ],
      image: '/help/screenshots/tables/column-settings.png',
      imageAlt: 'Column Settings in Quick Settings: Sort Active, Hide Active, Base Count, Labels Visible and Groups Visible.',
    },
    {
      key: 'titles-layout',
      name: 'Titles & Layout',
      what: 'Shows or hides the table’s titles (name, top, side, filter, weight and status), sets which title the top-left corner shows, and holds the table’s description.',
      how: 'In Quick Settings, open Titles & Layout, change the switches, then click Apply.',
      controls: ['Titles & Layout', 'Name', 'Top', 'Side', 'Filter', 'Weight', 'Status', 'Corner Priority', 'Net Resp'],
      tools: ['set_titles'],
      settings: ['Corner Priority: whether the corner shows the side title or the top title.'],
      image: '/help/screenshots/tables/titles-layout.png',
      imageAlt: 'Titles & Layout in Quick Settings: switches for Name, Top, Side, Filter, Weight and Status, then Corner Priority and Net Resp.',
    },
    {
      key: 'advanced-settings',
      name: 'Advanced',
      what: 'Five switches that change how the table’s figures are worked out. Most tables never need them.',
      how: 'In Quick Settings, open Advanced, change the switches, then click Apply.',
      controls: ['Advanced', 'Init as Missing', 'Exclude NE', 'Pad Hierarchics', 'Arith Over Stats', 'Full Stats'],
      settings: [
        'Init as Missing: a cell with no data shows as missing (blank) instead of 0.',
        'Exclude NE: people who gave no answer to the question are left out of the bases and statistics.',
        'Pad Hierarchics: for a question asked at more than one level (for example once per household, then once per person in it), each higher-level entry is counted even when nothing below it was answered.',
        'Arith Over Stats: on a row or column calculated from others (a sum or a difference), the statistics such as the mean come from the same calculation. Off, they are worked out again from the calculated figures.',
        'Full Stats: always works out the full set of statistics (including median, mode, minimum and maximum), not only the quicker totals.',
      ],
      tools: ['set_spec_properties'],
      image: '/help/screenshots/tables/advanced-settings.png',
      imageAlt: 'Advanced in Quick Settings: Init as Missing, Exclude NE, Pad Hierarchics, Arith Over Stats and Full Stats.',
    },
    {
      key: 'quick-settings-context',
      name: 'Context',
      what: 'The Table Analysis Prompt: notes that Insights reads when it analyses a table.',
      how: 'In Quick Settings, open Context, type in Table Analysis Prompt and click Save Context. It belongs to the open table. A new table has no Context section until it is saved; for a folder’s context, use Context on the folder’s ⋮ menu in the tree.',
      controls: ['Context', 'Table Analysis Prompt', 'Save Context'],
      agentNotes:
        'There is no page tool for this field; the person edits and saves it. The section shows and saves the context of the open table only, whatever the tree’s Context last opened. It is not shown for a new, unsaved table or an AutoCoder table. Folder, job and customer context are set with Context on the tree’s ⋮ menu (or in Admin → Context).',
      image: '/help/screenshots/tables/quick-settings-context.png',
      imageAlt: 'Context in Quick Settings: the Table Analysis Prompt box.',
    },
    {
      key: 'unsaved-changes',
      name: 'Save Settings and Save Table',
      what: 'A blue bar above the table when its view has changed (“View settings have changed”) or when it shows an unsaved result from Edit Spec (“Preview mode — changes are not saved yet”).',
      how: 'Click Discard to drop the changes. Click Save Settings to keep the view (what the cells show and the chart choices) on the table. Click Save Table to save it as a table: give it a name, pick a folder in My Tables and add an optional subtitle, then click Save As New. On your own table, Overwrite is on at first and the button reads Save.',
      controls: ['Discard', 'Save Settings', 'Save Table'],
      tools: ['discard_changes', 'save_view_settings', 'save_table'],
      settings: [
        'Save to: a Manager chooses My Tables or Exec (shared), and Exec saves with Save to Exec; everyone else saves to My Tables.',
        'Overwrite: shown only for your own table, and on at first. On, it replaces that table in its folder; off, the table is saved as a new one.',
      ],
      agentNotes:
        'discard_changes needs the user to click Confirm. save_table only opens the Save Table dialog: the user names the table and saves it. Save Settings saves on the user’s own tables. On a shared or colleague’s table it saves only for a Platinum administrator account and does nothing for anyone else, Managers included; offer Make your own copy instead.',
      image: '/help/screenshots/tables/unsaved-changes.png',
      imageAlt: 'The blue bar reading View settings have changed, with Discard, Save Settings and Save Table.',
    },
    {
      key: 'edit-spec-and-new-table',
      name: 'Edit Spec and New Table',
      what: 'Edit Spec opens the open table in the spec editor, to change its rows, columns, filter and weight. New Table opens the spec editor empty.',
      how: 'Click Edit Spec or New Table in the page header.',
      controls: ['Edit Spec', 'New Table'],
      tools: ['open_edit_spec', 'new_table'],
      image: '/help/screenshots/tables/edit-spec-and-new-table.png',
      imageAlt: 'The table header buttons Edit Spec, New Table and Insights.',
    },
    {
      key: 'insights',
      name: 'Insights',
      what: 'The Tables helper: a side pane that opens with a short summary of the open table, then a chat with this page’s controls. Ask about the table and it reads the numbers and answers; ask it to change settings, chart, filter, rename or copy and it does, with one Confirm card for each change that needs your click; ask for another table and it searches the job. With agent access the chat is the full Platinum agent. At the Insights pane level it is the Tables pane: it reads, filters and finds tables, and does not make web apps, decks, documents, saved tables or dashboards. The summary is made once per saved table and shared by everyone who opens it; a table opened with a filter gets a summary of the filtered figures, made each time. A filter applied later gets no new summary: the helper reports what changed. The summary reads the job’s analysis profile and ends with which version; a new version makes a new summary the next time the table is opened.',
      how: 'Click Insights in the page header: the question box stays locked while the summary is made (“Generating a summary of the table, one moment.”), then the summary appears first. Type a question to start a chat. Before your first message the pane offers starter questions as pills; click one to send it. Click Insights again, the X, or anywhere outside the pane to close it. Reopen it on the same table and the conversation carries on; on another table it starts afresh with that table’s summary. Open in full chat continues the conversation on the agent page, without the page’s controls; without agent access it is shown greyed with the reason. It is not offered while a table is in preview.',
      controls: ['Insights'],
      tools: ['toggle_insights'],
      agentNotes:
        'The pane opens at every AI level. A cached summary is shown to everyone; making a new one needs the insights level. At agent access the chat is the full agent (profile page-tables). At the insights level (level 2) it is the Tables pane (profile page-tables-insights, #1884): the page’s tools plus render_table, render_tables, render_chart and flag_advanced_analysis, a scratch home with no memory, Sonnet 5.5 whatever the picker says, and the server refuses it every write, files, dashboards, skills, chats, the warehouse, respondent rows and every saved output; its usage is logged as tables_pane (Tables pane on the usage pages) and a fixed-mode customer books one tables_pane_turn per message where its price list has one. Open in full chat is greyed with the reason below agent access. At level 1 the question box is disabled and says why. No chat is made until the person sends a message, and a helper chat raises no bell notice. Renames, copies, discards and filters each show the user one Confirm card; the agent calls the tool once and never asks first, and nothing the model says confirms them. There is no delete.',
      image: '/help/screenshots/tables/insights.png',
      imageAlt: 'The Insights pane: a written summary of the table’s key takeaways and the question box.',
    },
    {
      key: 'disabled-with-reason',
      name: 'Disabled with a reason',
      what: 'Across Platinum, an action you cannot do is never hidden: on tables and folders, dashboards, personas, brand themes, ThemeAI tasks and code frames, and chats. It shows greyed, with one sentence that says what you cannot do, who to ask (the owner, or your team manager), and, where a copy is possible, “or make your own copy”.',
      how: 'In Tables, open the ⋮ menu on a table or folder: a greyed item carries its reason underneath. Use Make your own copy to get a copy you can change.',
      agentNotes:
        'The page tools refuse with the menu’s own sentence; repeat it as given. Shared tables are changed by Managers; a colleague’s tables by their owner only.',
    },
    {
      key: 'edit-spec-page',
      name: 'Edit Spec',
      what: 'The spec editor: builds a table from the job’s variables. It sets the side axis (rows), the top axis (columns), the filter, the weight and the case filter.',
      how: 'Tick variables in the Variable Tree on the left (Search variables... narrows it), then press the green + on the Side Axis Editor or Top Axis Editor to add them. Each item’s ⋮ menu has Up, Down, Top, Bottom, Add Filter, Edit Filter, Add/Remove Bases, Copy and Remove, and each axis’s syntax can be edited as text. Use Add filter and Add weight in the Filter and Weighting bar. Settings opens Quick Settings. Click Run Spec to see the result on the Tables page in preview, then save it there with Save Table. Cancel goes back without saving. Edit Spec helper opens the agent in a side pane to build the spec with you.',
      settings: ['Data source: Managers can build from Survey Data or AutoCoder Data.'],
      controls: [
        'Run Spec', 'Settings', 'Cancel', 'Save Table', 'Edit Spec helper',
        'Add filter', 'Filter', 'Edit filter', 'Clear filter', 'Add weight', 'Weight', 'Clear weight', 'Case Filter', 'Clear case filter',
        'Search variables...', 'Data source', 'Collapse sidebar',
        'Add selected to Side Axis', 'Add selected to Top Axis', 'Syntax', 'Edit Side Axis syntax', 'Edit Top Axis syntax', 'Apply', 'More actions for …',
        'Move', 'Up', 'Down', 'Top', 'Bottom', 'Add Filter', 'Edit Filter', 'Add/Remove Bases', 'Copy', 'Remove',
      ],
      tools: ['read_spec', 'find_variable', 'add_variable_to_axis', 'move_axis_item', 'remove_axis_item', 'set_spec_filter', 'set_spec_weight', 'open_spec_settings', 'run_spec', 'cancel_edit_spec'],
      agentNotes:
        'Edit Spec does not save a table by itself: Run Spec opens the result on the Tables page as a preview, and Save Table there saves it; Discard puts the table back as it was. ' +
        'The page tools read the spec (read_spec), find variables (find_variable), add a variable with all its codes to the rows or columns (add_variable_to_axis), move or remove an item (move_axis_item, remove_axis_item), set the weight, open Settings and run the spec. ' +
        'A filter needs the user’s yes before it is set (set_spec_filter asks first), and Cancel needs their Confirm click because it throws the edits away. ' +
        'The Edit Spec helper button opens the agent in a side pane with these tools; it follows agent access (disabled with the reason below it) and starts a fresh chat each time. It is not offered inside a dashboard’s Edit Data. A filter or Cancel shows a Confirm card in the pane. ' +
        'Typing axis syntax, Add Filter on an item, bases and copies stay with the user. Save Table appears only when Edit Spec is opened from a dashboard’s Edit Data, where no page tool runs.',
      image: '/help/screenshots/tables/edit-spec-page.png',
      imageAlt: 'The Edit Spec page: the variable tree on the left, the Filter and Weighting bar, and the Side Axis and Top Axis editors.',
    },
    {
      key: 'multi-tables',
      name: 'Multi-Tables',
      what: 'A multi-table list: a named set of tables read together, one tab per table, with a global filter that applies to all of them. Saved lists appear under My Table Lists in the tree.',
      how: 'Add tables with Add to multi-table. Name the list in Multi-Table List name, set a Global filter if you want one, and click Save. Click a tab to open that table. Once the list is saved, XLSX downloads all the tables, Insights runs a Multi-Table Analysis with a persona and writes a combined report you can question, and Delete removes the list, not its tables.',
      agentNotes:
        'Multi-Table Analysis follows the customer’s AI access rules for Gen AI cards. Deleting a list leaves its tables in place.',
      image: '/help/screenshots/tables/multi-tables.png',
      imageAlt: 'A saved multi-table list: Multi-Table List name, Global filter, Cancel, Delete, Insights, XLSX and Save, a tab per table, and the second table open.',
    },
    {
      key: 'batch-generate',
      name: 'Batch Generate Tables',
      what: 'Makes many tables at once: one table per variable you pick, each against the same top axis, filter, weight and case filter.',
      how: 'Open Batch Generate from the Tables menu in the top bar. Tick variables in the tree (Select All and Clear All help) and press + to add them to Tables to Create. Build the top axis in the Top Axis Editor and set the filter and weight. Set a name prefix or suffix and the destination folder. Click Review & Generate, check the list, then click Generate.',
      settings: [
        'Table Name Prefix and Table Name Suffix: added to each variable’s name to name its table.',
        'Destination Folder: a folder in My Tables; a Manager can also choose the shared tables.',
      ],
      image: '/help/screenshots/tables/batch-generate.png',
      imageAlt: 'The Batch Generate Tables page: the variable tree, batch settings, filter and weighting, Tables to Create and the Top Axis Editor.',
    },
    {
      key: 'regenerate-tables',
      name: 'Regenerate Tables',
      what: 'Makes a new version of every table in a folder with changes applied to all of them: a new top axis, filter, weight or case filter, cell contents or decimal places.',
      how: 'Open the ⋮ menu on a folder and choose Regenerate tables. It is a walkthrough with one question per step, and Back on every step. For a folder in your own My Tables, first choose Overwrite these tables or Create a new set. For a new set, give a name prefix (the suffix is optional); the page shows where the tables go and the first new names. Then choose what to change, and review: "You’re about to generate N tables. Continue?" Click Go. The page counts the tables as they are saved ("Generating tables: 40 of 171"), then opens Tables.',
      controls: ['Regenerate tables'],
      settings: [
        'Overwrite these tables or Create a new set: only for a folder in your own My Tables. Overwrite replaces those tables in place and cannot be undone.',
        'Top: Keep original or Replace with (opens the axis picker). A table always needs a top, so there is no Remove all.',
        'Filter: Keep original, Replace with, Append (ANDed with each table’s own filter) or Remove all.',
        'Weight: Keep original, Replace with (search the job’s variables, pick one used by the folder’s tables, or type a name; it is written as name()) or Remove all.',
        'Case filter: Keep original, Replace with (one variable that is not hierarchic, and its codes) or Remove all. A new case filter is tried on the first table, with a warning when no cases pass it.',
        'Cell Display: Keep original or Change (Frequencies, Column % and Row %; keep at least one on).',
        'Decimal places: Keep original or Change (Frequencies and Percents, 0 to 9).',
      ],
      agentNotes:
        'Without Overwrite, the new tables go to a new folder in the user’s My Tables named with the prefix. The originals are not changed.',
      image: '/help/screenshots/tables/regenerate-tables.png',
      imageAlt: 'The ⋮ menu of the Waves folder with Regenerate tables highlighted.',
    },
  ],
  steps: [
    'Open the job’s tables and find the table in the Tables tree, or search for it.',
    'Read it as a table, or click Chart View to see it as a chart.',
    'Use Quick Filter and Quick Settings to focus the table and change what it shows.',
    'Save Settings to keep the view, or Save Table to keep a changed table in My Tables.',
    'Download it to Excel or export the chart to PowerPoint.',
  ],
  // Shown as pills in this section's page helpers and the Help pane until the first message (Admin → System context replaces them per customer).
  starters: [
    'What does this table show?',
    'Help me find a table',
    'Filter this to one group of people',
    'Show this as a chart',
  ],
  intro: 'What would you like to know about this table?',
}
